import {
  Box,
  Button,
  MenuItem,
  InputLabel,
  Menu,
} from "@mui/material";
import PropTypes from "prop-types";
import { useState, useMemo } from "react";
import { useUser } from "../hooks/user.js";
import ClaimStatusBadge from "./ClaimStatusBadge.jsx";
import { useClaimActions } from "../hooks/useClaimActions.jsx";
import React from "react";

ClaimStatusChanger.propTypes = {
  claim: PropTypes.object.isRequired,
  onStatusChange: PropTypes.func,
};

/**
 * Komponente zum Ändern des Status eines Schadens
 *
 * @param claim
 * @param onStatusChange
 * @returns {React.JSX.Element|null}
 * @constructor
 */
export default function ClaimStatusChanger({
  claim,
  onStatusChange,
}) {

  const { updateClaimStatus } = useClaimActions();

  const currentStatusObject = claim?.status; // Ursprünglicher Status
  const possibleTransitions = claim?.possible_status_transitions;

  const statusOptions = useMemo(() => {
    const allOptions = [];
    if (currentStatusObject) {
      allOptions.push(currentStatusObject);
    }
    if (possibleTransitions) {
      allOptions.push(...possibleTransitions);
    }
    const uniqueOptions = allOptions.reduce((acc, current) => {
      if (!acc.find((item) => item.value === current.value)) {
        acc.push(current);
      }
      return acc;
    }, []);
    return uniqueOptions;
  }, [currentStatusObject, possibleTransitions]);

  const [status, setStatus] = useState(claim?.status || { value: "", label: "" });

  const [isLoading, setIsLoading] = useState(false);
  const { user } = useUser();

  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenuOpen = (event, id) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleStatusChange = async () => {

    await updateClaimStatus(claim.id, status.value, {
      label: "Status ändern",
      setIsLoading,
    });

    if (onStatusChange) onStatusChange(status);
  };

  if (
    !(
      user.role_names.includes("sysadmin") ||
      user.role_names.includes("makler_sachbearbeitung")
    )
  ) {
    return null; // Keine Berechtigung, den Status zu ändern
  }

  return (
    <Box
      display="flex"
      flexDirection="row"
      alignItems="center"
      justifyContent="flex-end"
    >
      <Box>
        <InputLabel
          id="schaden-state-label"
          sx={{ color: "text.default", fontSize: 14 }}
        >
          Status
        </InputLabel>

        <ClaimStatusBadge
          status={status.value}
          label={status.label}
          onClick={(e) => handleMenuOpen(e)}
          open={Boolean(anchorEl)}
          disabled={isLoading}
          ariaLabelledby="schaden-state-label"
        />

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          {(statusOptions || []).map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              onClick={(e) => {
                setStatus(option);
                handleMenuClose();
              }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  display: "inline-block",
                  marginRight: 1,
                }}
              />
              {option.label}
            </MenuItem>
          ))}
        </Menu>
      </Box>

      {status.value !== currentStatusObject?.value && (
        <Box marginLeft={2} marginTop={2.5}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleStatusChange}
            disabled={isLoading}
            size="medium"
          >
            {isLoading ? "Wird gespeichert..." : "Status ändern"}
          </Button>
        </Box>
      )}
    </Box>
  );
}
